import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Eye,
  Calendar,
  CalendarDays,
  User,
  Store,
  Receipt,
  BadgeInfo,
  IndianRupee,
  ShoppingCart,
  Wrench,
  Clock,
  MapPin,
  Phone,
  Mail,
  Copy,
  CheckCircle2,
  AlertCircle,
  Package,
  ArrowLeft,
  CreditCard,
  Users
} from 'lucide-react';
import apiWithAuth from '../../services/apiWithAuth';
import serviceBookingAdminApi from '../../services/serviceBookingAdminApi';

const BOOKINGS_PER_PAGE = 10;

const BOOKING_STATUS_OPTIONS = {
  PENDING_PROVIDER_CONFIRMATION: 'Pending Provider Confirmation',
  AWAITING_USER_SELECTION: 'Awaiting User Selection',
  PROVIDER_REJECTED: 'Provider Rejected',
  AWAITING_PAYMENT: 'Awaiting Payment',
  PLACED: 'Placed',
  ASSIGNED: 'Assigned',
  PROVIDER_REACHED: 'Provider Reached',
  STARTED: 'Started',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired'
};

const PAYMENT_STATUS_OPTIONS = {
  PENDING: 'Pending',
  PAID: 'Paid',
  FAILED: 'Failed'
};

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatCurrency(amount) {
  if (amount === null || amount === undefined || amount === '') return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMoney(amount) {
  return formatCurrency(amount) || '₹0.00';
}

function displayText(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  return value;
}

function formatStatusLabel(status) {
  if (!status) return 'Unknown';
  return BOOKING_STATUS_OPTIONS[status] || String(status).replace(/_/g, ' ');
}

function getStatusTone(status) {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'CANCELLED':
    case 'EXPIRED':
    case 'PROVIDER_REJECTED':
    case 'FAILED':
      return 'danger';
    case 'PLACED':
    case 'ASSIGNED':
    case 'PROVIDER_REACHED':
    case 'STARTED':
    case 'PAID':
      return 'info';
    case 'AWAITING_PAYMENT':
    case 'AWAITING_USER_SELECTION':
    case 'PENDING_PROVIDER_CONFIRMATION':
    case 'PENDING':
      return 'warning';
    default:
      return 'neutral';
  }
}

const toneClasses = {
  success: 'bg-green-100 text-green-800',
  danger: 'bg-red-100 text-red-800',
  warning: 'bg-yellow-100 text-yellow-800',
  info: 'bg-blue-100 text-blue-800',
  neutral: 'bg-gray-100 text-gray-700'
};

function StatusPill({ label, tone = 'neutral', size = 'sm' }) {
  const sizeClass = size === 'md' ? 'px-3 py-1 text-sm' : 'px-2.5 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center rounded-full font-medium ${sizeClass} ${toneClasses[tone] || toneClasses.neutral}`}>
      {label}
    </span>
  );
}

function BookingStatusBadge({ status, size = 'sm' }) {
  return <StatusPill label={formatStatusLabel(status)} tone={getStatusTone(status)} size={size} />;
}

function EmptyHint({ children }) {
  return <p className="text-sm text-gray-400 italic">{children}</p>;
}

function MetaRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-50 last:border-0">
      <dt className="text-xs font-medium text-slate-500 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-800 text-right break-words">{value ?? '—'}</dd>
    </div>
  );
}

function DetailSection({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-slate-500" />}
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="px-4 py-4">{children}</div>
    </div>
  );
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(text));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
    >
      {copied ? <CheckCircle2 className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function ContactLink({ phone }) {
  if (!phone) return null;
  return (
    <a href={`tel:${phone}`} className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900">
      <Phone className="h-3 w-3" />
      {phone}
    </a>
  );
}

function AddressBlock({ address, emptyLabel = 'No address mentioned' }) {
  if (!address) return <EmptyHint>{emptyLabel}</EmptyHint>;
  const line = [address.buildingNo, address.street, address.city, address.state, address.postalCode || address.pincode]
    .filter(Boolean)
    .join(', ');
  const hasAny = line || address.contactName || address.contactPhone || address.label;
  if (!hasAny) return <EmptyHint>{emptyLabel}</EmptyHint>;
  return (
    <div className="space-y-1 text-sm text-gray-700">
      {address.label && <p className="font-medium text-slate-800">{address.label}</p>}
      {address.contactName && <p>{address.contactName}</p>}
      {address.contactPhone && <ContactLink phone={address.contactPhone} />}
      {line && <p>{line}</p>}
      {(address.latitude != null || address.longitude != null) && (
        <p className="text-xs text-slate-500">
          Lat {displayText(address.latitude)} · Lng {displayText(address.longitude)}
        </p>
      )}
    </div>
  );
}

export default function ServiceBookingManagement() {
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [viewMode, setViewMode] = useState('list');
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookingsCount, setTotalBookingsCount] = useState(0);

  const [searchCustomerName, setSearchCustomerName] = useState('');
  const [searchShopName, setSearchShopName] = useState('');
  const [searchBookingId, setSearchBookingId] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [scheduleType, setScheduleType] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const isInitialMount = useRef(true);
  const fetchTimeoutRef = useRef(null);

  const fetchBookings = useCallback(async (pageToFetch = currentPage) => {
    setLoading(true);
    setError(null);

    let endpoint = '/admin-panel/service-bookings/bookings';
    const params = {};

    const hasSearch = searchCustomerName.trim() || searchShopName.trim() || searchBookingId.trim();
    const hasFilter =
      filterStatus !== 'all' ||
      filterPaymentStatus !== 'all' ||
      scheduleType !== 'all' ||
      dateRange.start ||
      dateRange.end ||
      minPrice ||
      maxPrice;

    if (hasSearch) {
      endpoint = '/admin-panel/service-bookings/bookings/search';
      if (searchCustomerName.trim()) params.customerName = searchCustomerName.trim();
      if (searchShopName.trim()) params.shopName = searchShopName.trim();
      if (searchBookingId.trim()) params.bookingId = searchBookingId.trim();
      if (filterStatus !== 'all') params.bookingStatus = filterStatus;
      if (filterPaymentStatus !== 'all') params.paymentStatus = filterPaymentStatus;
      if (scheduleType !== 'all') params.scheduleType = scheduleType;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
    } else if (hasFilter) {
      endpoint = '/admin-panel/service-bookings/bookings/filter';
      if (filterStatus !== 'all') params.bookingStatus = filterStatus;
      if (filterPaymentStatus !== 'all') params.paymentStatus = filterPaymentStatus;
      if (scheduleType !== 'all') params.scheduleType = scheduleType;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
    }

    params.page = pageToFetch;
    params.limit = BOOKINGS_PER_PAGE;

    try {
      const response = await apiWithAuth.get(endpoint, { params });
      const data = response.data;
      setBookings(data.bookings ?? []);
      setTotalPages(data.totalPages ?? 1);
      setCurrentPage(data.currentPage ?? 1);
      setTotalBookingsCount(data.totalBookings ?? 0);
    } catch (err) {
      console.error('Failed to fetch service bookings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch bookings');
      setBookings([]);
      setTotalPages(1);
      setTotalBookingsCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    searchCustomerName,
    searchShopName,
    searchBookingId,
    filterStatus,
    filterPaymentStatus,
    scheduleType,
    dateRange.start,
    dateRange.end,
    minPrice,
    maxPrice
  ]);

  useEffect(() => {
    if (!authToken) return;

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);

    if (isInitialMount.current) {
      fetchBookings(1);
      isInitialMount.current = false;
      return;
    }

    if (currentPage === 1) {
      fetchTimeoutRef.current = setTimeout(() => fetchBookings(1), 300);
    } else {
      setCurrentPage(1);
    }

    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, [
    authToken,
    searchCustomerName,
    searchShopName,
    searchBookingId,
    filterStatus,
    filterPaymentStatus,
    scheduleType,
    dateRange.start,
    dateRange.end,
    minPrice,
    maxPrice,
    fetchBookings
  ]);

  useEffect(() => {
    if (!authToken || isInitialMount.current || currentPage === 1) return;
    fetchBookings(currentPage);
  }, [currentPage, authToken, fetchBookings]);

  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
  }, []);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleViewDetails = async (booking) => {
    if (!booking?.bookingId) return;
    setSelectedBooking(booking);
    setDetailTab('overview');
    setViewMode('detail');
    setLoadingDetails(true);
    setError(null);
    try {
      const response = await serviceBookingAdminApi.getServiceBooking(booking.bookingId);
      const full = serviceBookingAdminApi.extractBookingFromResponse(response.data);
      if (full?.bookingId) setSelectedBooking(full);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load booking details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedBooking(null);
    setError(null);
    setSuccessMessage(null);
  };

  const clearFilters = () => {
    setSearchCustomerName('');
    setSearchShopName('');
    setSearchBookingId('');
    setFilterStatus('all');
    setFilterPaymentStatus('all');
    setScheduleType('all');
    setDateRange({ start: '', end: '' });
    setMinPrice('');
    setMaxPrice('');
  };

  const providerUser = selectedBooking?.provider?.user || selectedBooking?.providerId?.user;
  const showingFrom = totalBookingsCount === 0 ? 0 : (currentPage - 1) * BOOKINGS_PER_PAGE + 1;
  const showingTo = Math.min(currentPage * BOOKINGS_PER_PAGE, totalBookingsCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {viewMode === 'list' && (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Service Booking Management</h1>
              <p className="mt-1 text-sm text-gray-500">Monitor and review customer service bookings across shops</p>
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {successMessage && (
              <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
                {successMessage}
              </div>
            )}

            <div className="mb-4 text-sm text-gray-600">
              Total Bookings: <span className="font-semibold text-gray-900">{totalBookingsCount}</span>
            </div>

            <div className="bg-white shadow rounded-lg mb-6 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-800">Filters & Search</h2>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs font-medium text-sky-700 hover:text-sky-900"
                >
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <User className="h-4 w-4 text-gray-500" />
                    Customer name
                  </label>
                  <input
                    type="text"
                    placeholder="Customer name..."
                    value={searchCustomerName}
                    onChange={(e) => setSearchCustomerName(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Store className="h-4 w-4 text-gray-500" />
                    Shop name
                  </label>
                  <input
                    type="text"
                    placeholder="Shop name..."
                    value={searchShopName}
                    onChange={(e) => setSearchShopName(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Receipt className="h-4 w-4 text-gray-500" />
                    Booking ID
                  </label>
                  <input
                    type="text"
                    placeholder="Booking ID..."
                    value={searchBookingId}
                    onChange={(e) => setSearchBookingId(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <BadgeInfo className="h-4 w-4 text-gray-500" />
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="all">All Status</option>
                    {Object.entries(BOOKING_STATUS_OPTIONS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <CreditCard className="h-4 w-4 text-gray-500" />
                    Payment Status
                  </label>
                  <select
                    value={filterPaymentStatus}
                    onChange={(e) => setFilterPaymentStatus(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="all">All Payments</option>
                    {Object.entries(PAYMENT_STATUS_OPTIONS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <CalendarDays className="h-4 w-4 text-gray-500" />
                    From Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <CalendarDays className="h-4 w-4 text-gray-500" />
                    To Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <IndianRupee className="h-4 w-4 text-gray-500" />
                    Min Price
                  </label>
                  <input
                    type="number"
                    placeholder="Min price..."
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <IndianRupee className="h-4 w-4 text-gray-500" />
                    Max Price
                  </label>
                  <input
                    type="number"
                    placeholder="Max price..."
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <ShoppingCart className="h-4 w-4 text-gray-500" />
                    Schedule Type
                  </label>
                  <select
                    value={scheduleType}
                    onChange={(e) => setScheduleType(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="INSTANT">Instant</option>
                    <option value="SLOT">Slot</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg">
              {loading ? (
                <div className="p-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4" />
                  Loading bookings...
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Services</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {bookings.length > 0 ? (
                          bookings.map((booking) => (
                            <tr key={booking._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                #{booking.bookingId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {booking.userId?.fullName || <span className="text-gray-400 italic">Not available</span>}
                                  </span>
                                  {booking.userId?.phone && (
                                    <span className="text-gray-500 text-xs">{booking.userId.phone}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {booking.shopId?.name || <span className="text-gray-400 italic">Not selected</span>}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(booking.createdAt) || <span className="text-gray-400 italic">-</span>}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <BookingStatusBadge status={booking.status} size="sm" />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {booking.itemCount || booking.services?.length || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(booking.grandTotal) || <span className="text-gray-400">₹0.00</span>}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  type="button"
                                  onClick={() => handleViewDetails(booking)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View details"
                                >
                                  <Eye className="h-5 w-5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="p-12 text-center">
                              <Wrench className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
                              <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <nav className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                      <div className="hidden sm:block">
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{showingFrom}</span> to{' '}
                          <span className="font-medium">{showingTo}</span> of{' '}
                          <span className="font-medium">{totalBookingsCount}</span> results
                        </p>
                      </div>
                      <div className="flex-1 flex justify-between sm:justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </nav>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {viewMode === 'detail' && selectedBooking && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b bg-white flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleBackToList}
                className="inline-flex items-center text-sm font-medium text-sky-700 hover:text-sky-900"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Bookings
              </button>
              {loadingDetails && (
                <span className="text-xs text-slate-500 animate-pulse">Refreshing details…</span>
              )}
            </div>

            {error && (
              <div className="mx-4 sm:mx-6 mt-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">
                  Booking #{selectedBooking.bookingId}
                </h1>
                <div className="flex items-center flex-wrap gap-2 mt-2">
                  <BookingStatusBadge status={selectedBooking.status} size="md" />
                  {selectedBooking.scheduleType && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 capitalize flex items-center">
                      <Clock className="mr-1" size={14} />
                      {selectedBooking.scheduleType}
                    </span>
                  )}
                  {selectedBooking.paymentStatus && (
                    <StatusPill
                      label={`Payment: ${selectedBooking.paymentStatus}`}
                      tone={getStatusTone(selectedBooking.paymentStatus)}
                      size="md"
                    />
                  )}
                  {formatDate(selectedBooking.createdAt) && (
                    <span className="text-sm text-gray-500 flex items-center">
                      <Calendar className="inline mr-1" size={14} />
                      {formatDate(selectedBooking.createdAt)}
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shrink-0 min-w-[180px] shadow-sm">
                <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Booking total</p>
                <p className="text-3xl font-bold mt-1 text-slate-900 tabular-nums">
                  {formatMoney(selectedBooking.grandTotal)}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedBooking.itemCount ?? selectedBooking.services?.length ?? 0} service(s)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-100 border-b border-slate-100">
              {[
                {
                  label: 'Customer',
                  name: selectedBooking.userId?.fullName,
                  phone: selectedBooking.userId?.phone,
                  sub: selectedBooking.userId?.email
                },
                {
                  label: 'Shop',
                  name: selectedBooking.shopId?.name,
                  phone: selectedBooking.shopId?.phone,
                  sub: selectedBooking.shopId?.gstNumber ? `GSTIN ${selectedBooking.shopId.gstNumber}` : null
                },
                {
                  label: 'Provider',
                  name: providerUser?.fullName || selectedBooking.technician?.name,
                  phone: providerUser?.phone || selectedBooking.technician?.phone,
                  sub: selectedBooking.provider?.position || selectedBooking.providerId?.position
                },
                {
                  label: 'Category',
                  name: selectedBooking.parentCategoryId?.name,
                  phone: null,
                  sub: selectedBooking.couponCode ? `Coupon ${selectedBooking.couponCode}` : null
                }
              ].map((card) => (
                <div key={card.label} className="px-4 py-4 bg-white hover:bg-slate-50/80 transition-colors">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{card.label}</p>
                  <p className="text-[15px] font-semibold text-slate-900 mt-1.5 truncate leading-snug">
                    {displayText(card.name, 'Not assigned')}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 min-h-[22px]">
                    {card.phone ? (
                      <>
                        <ContactLink phone={card.phone} />
                        <CopyButton text={card.phone} label="Copy" />
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">No phone</span>
                    )}
                  </div>
                  {card.sub ? (
                    <p className="text-xs text-slate-500 mt-1.5 truncate">{card.sub}</p>
                  ) : (
                    <p className="text-xs text-slate-300 mt-1.5">—</p>
                  )}
                </div>
              ))}
            </div>

            <div className="border-b border-slate-200 px-4 sm:px-6">
              <nav className="-mb-px flex gap-6 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'fulfillment', label: 'Fulfillment' },
                  { id: 'billing', label: 'Payment & Billing' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setDetailTab(tab.id)}
                    className={`whitespace-nowrap py-3 border-b-2 text-sm font-medium ${
                      detailTab === tab.id
                        ? 'border-sky-600 text-sky-800'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4 sm:p-6 bg-slate-50/40">
              {detailTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <DetailSection title="Services" icon={Package}>
                    {(selectedBooking.services || []).length === 0 ? (
                      <EmptyHint>No services on this booking</EmptyHint>
                    ) : (
                      <div className="space-y-3">
                        {selectedBooking.services.map((service, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
                            <div className="flex justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{displayText(service.name)}</p>
                                {service.shortDescription && (
                                  <p className="text-xs text-slate-500 mt-0.5">{service.shortDescription}</p>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                  Qty {displayText(service.quantity, 0)} · {displayText(service.durationMinutes, 0)} min
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-semibold text-slate-900">{formatMoney(service.totalPrice)}</p>
                                <p className="text-xs text-slate-500">{formatMoney(service.price)} each</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </DetailSection>

                  <DetailSection title="Schedule & Address" icon={MapPin}>
                    <dl>
                      <MetaRow label="Type" value={displayText(selectedBooking.schedule?.type || selectedBooking.scheduleType)} />
                      <MetaRow label="Date" value={displayText(formatDate(selectedBooking.schedule?.date))} />
                      <MetaRow label="Time slot" value={displayText(selectedBooking.schedule?.timeSlot, 'N/A')} />
                      <MetaRow label="Provider confirm by" value={displayText(formatDate(selectedBooking.providerConfirmationDeadline))} />
                      <MetaRow label="User select by" value={displayText(formatDate(selectedBooking.userSelectionDeadline))} />
                    </dl>
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Service address</p>
                      <AddressBlock address={selectedBooking.address || selectedBooking.customerAddress} />
                    </div>
                  </DetailSection>

                  <DetailSection title="Customer" icon={User}>
                    <dl>
                      <MetaRow label="Name" value={displayText(selectedBooking.userId?.fullName)} />
                      <MetaRow
                        label="Phone"
                        value={selectedBooking.userId?.phone
                          ? <span className="inline-flex items-center gap-2"><Phone className="h-3 w-3" />{selectedBooking.userId.phone}</span>
                          : '—'}
                      />
                      <MetaRow
                        label="Email"
                        value={selectedBooking.userId?.email
                          ? <span className="inline-flex items-center gap-2"><Mail className="h-3 w-3" />{selectedBooking.userId.email}</span>
                          : '—'}
                      />
                    </dl>
                  </DetailSection>

                  <DetailSection title="Shop" icon={Store}>
                    {!selectedBooking.shopId?.name ? (
                      <EmptyHint>Shop not selected yet</EmptyHint>
                    ) : (
                      <dl>
                        <MetaRow label="Name" value={displayText(selectedBooking.shopId?.name)} />
                        <MetaRow label="Phone" value={displayText(selectedBooking.shopId?.phone)} />
                        <MetaRow label="GST" value={displayText(selectedBooking.shopId?.gstNumber)} />
                        <MetaRow
                          label="City"
                          value={displayText(selectedBooking.shopId?.address?.city)}
                        />
                      </dl>
                    )}
                  </DetailSection>
                </div>
              )}

              {detailTab === 'fulfillment' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <DetailSection title="Lifecycle" icon={Clock}>
                    <dl>
                      <MetaRow label="Status" value={<BookingStatusBadge status={selectedBooking.status} />} />
                      <MetaRow label="Reached at" value={displayText(formatDate(selectedBooking.reachedAt))} />
                      <MetaRow label="Started at" value={displayText(formatDate(selectedBooking.startedAt))} />
                      <MetaRow label="Completed at" value={displayText(formatDate(selectedBooking.completedAt))} />
                      <MetaRow label="Cancelled at" value={displayText(formatDate(selectedBooking.cancelledAt))} />
                      <MetaRow label="Cancel reason" value={displayText(selectedBooking.cancellationReason)} />
                      <MetaRow label="OTP" value={displayText(selectedBooking.verificationOtp)} />
                    </dl>
                  </DetailSection>

                  <DetailSection title="Assigned provider" icon={Users}>
                    {!providerUser && !selectedBooking.technician?.name ? (
                      <EmptyHint>No provider assigned</EmptyHint>
                    ) : (
                      <dl>
                        <MetaRow label="Name" value={displayText(providerUser?.fullName || selectedBooking.technician?.name)} />
                        <MetaRow label="Phone" value={displayText(providerUser?.phone || selectedBooking.technician?.phone)} />
                        <MetaRow label="Email" value={displayText(providerUser?.email)} />
                        <MetaRow label="Position" value={displayText(selectedBooking.provider?.position || selectedBooking.providerId?.position)} />
                      </dl>
                    )}
                  </DetailSection>

                  <DetailSection title="Provider confirmations" icon={Users}>
                    {(selectedBooking.providerConfirmations || []).length === 0 ? (
                      <EmptyHint>No provider confirmation requests</EmptyHint>
                    ) : (
                      <div className="space-y-2">
                        {selectedBooking.providerConfirmations.map((row, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-slate-800">
                                {displayText(row.shopName || row.shop?.name, 'Shop')}
                              </span>
                              <StatusPill
                                label={displayText(row.status)}
                                tone={
                                  row.status === 'confirmed' ? 'success'
                                    : row.status === 'rejected' ? 'danger'
                                      : 'warning'
                                }
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Quote: {formatMoney(row.paymentQuote?.finalAmount)}
                              {row.confirmedAt ? ` · Confirmed ${formatDate(row.confirmedAt)}` : ''}
                              {row.rejectedAt ? ` · Rejected ${formatDate(row.rejectedAt)}` : ''}
                            </p>
                            {row.notes && <p className="text-xs text-slate-600 mt-1">{row.notes}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </DetailSection>

                  <DetailSection title="Rating" icon={CheckCircle2}>
                    {!selectedBooking.rating?.value ? (
                      <EmptyHint>Not rated yet</EmptyHint>
                    ) : (
                      <dl>
                        <MetaRow label="Rating" value={`${selectedBooking.rating.value} / 5`} />
                        <MetaRow label="Comment" value={displayText(selectedBooking.rating.comment)} />
                        <MetaRow label="Rated at" value={displayText(formatDate(selectedBooking.rating.ratedAt))} />
                      </dl>
                    )}
                  </DetailSection>
                </div>
              )}

              {detailTab === 'billing' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <DetailSection title="Payment summary" icon={CreditCard}>
                    <dl>
                      <MetaRow label="Item total" value={formatMoney(selectedBooking.itemTotal ?? selectedBooking.payment?.itemTotal)} />
                      <MetaRow label="Platform fee" value={formatMoney(selectedBooking.platformFee ?? selectedBooking.payment?.platformFee)} />
                      <MetaRow label="GST" value={formatMoney(selectedBooking.gstAmount ?? selectedBooking.payment?.gst)} />
                      <MetaRow label="Discount" value={formatMoney(selectedBooking.discountAmount ?? selectedBooking.payment?.discount)} />
                      <MetaRow label="Final amount" value={<span className="font-semibold">{formatMoney(selectedBooking.grandTotal)}</span>} />
                      <MetaRow
                        label="Payment status"
                        value={<StatusPill label={displayText(selectedBooking.paymentStatus)} tone={getStatusTone(selectedBooking.paymentStatus)} />}
                      />
                      <MetaRow label="Method" value={displayText(selectedBooking.paymentMethod ?? selectedBooking.payment?.method)} />
                      <MetaRow label="Coupon" value={displayText(selectedBooking.couponCode)} />
                    </dl>
                  </DetailSection>

                  <DetailSection title="Gateway details" icon={Receipt}>
                    <dl>
                      <MetaRow label="Payment order ID" value={displayText(selectedBooking.payment?.paymentOrderId)} />
                      <MetaRow label="Payment ID" value={displayText(selectedBooking.paymentId ?? selectedBooking.payment?.paymentId)} />
                      <MetaRow label="Initiated at" value={displayText(formatDate(selectedBooking.payment?.initiatedAt))} />
                      <MetaRow label="Paid at" value={displayText(formatDate(selectedBooking.payment?.paidAt))} />
                      <MetaRow label="Failed at" value={displayText(formatDate(selectedBooking.payment?.failedAt))} />
                    </dl>
                    {(selectedBooking.payment?.statusTransitions || []).length > 0 && (
                      <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Transitions</p>
                        {selectedBooking.payment.statusTransitions.map((t, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-slate-600">
                            <span>{t.status} · {t.source}</span>
                            <span>{formatDate(t.at) || '—'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </DetailSection>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
